/**
 * useSelfFieldAdapter — worker-self реализация `EntryEditorAdapter` (задача 77 §1.1).
 *
 * Вся worker-only механика записи, вынесенная из `NewEntryFlow.tsx`, живёт
 * здесь — ядро её не видит:
 *   - `createFieldEntry`/`replaceFieldEntry`/`deleteFieldEntry` (self field-API);
 *   - offline-enqueue на network-error (TypeError/Failed-to-fetch) →
 *     `enqueue(user.id, …)` + toast «Сохранено локально» + onClose;
 *   - спец-кейс PUT 409 → toast.conflict + invalidate + onClose;
 *   - порядок `mutate → invalidate(['timesheet', date]) → onSaved/onClose`.
 *
 * PIN-подпись и `drainQueue`-перед-подписью тут НЕТ — они принадлежат дневной
 * оболочке (`FieldSignPage`), а не редактору записи.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { createFieldEntry, deleteFieldEntry, replaceFieldEntry } from "@/api/field-timesheet";
import type { TimesheetEntry, TimesheetEntryPayload } from "@/api/field-timesheet";
import { enqueue, isNetworkError } from "@/lib/field-offline-queue";
import { apiErrorFormIssue, type FormValidationIssue } from "@/components/ui/form-validation";
import { showApiErrorToast } from "@/lib/api-error-toast";
import { presentApiError } from "@/lib/api-error-policy";
import { useMe } from "@/hooks/useMe";

import type { EntryEditorAdapter } from "./types";

export interface SelfFieldAdapterOptions {
  /** Активная дата таймшита (для query-key invalidate + qs API). */
  activeDate: string;
  /** Закрыть редактор (× / успешный enqueue / 409 / delete). */
  onClose: () => void;
  /** После успешного POST/PUT с новой entry. */
  onSaved: (entry: TimesheetEntry) => void;
  /** После успешного DELETE. */
  onDeleted?: () => void;
}

export function useSelfFieldAdapter({
  activeDate,
  onClose,
  onSaved,
  onDeleted,
}: SelfFieldAdapterOptions): EntryEditorAdapter {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useMe();
  const [persistentSaveError, setPersistentSaveError] = useState<FormValidationIssue | null>(null);

  // Сетевой fallback (§4.4): на TypeError/Failed-to-fetch кладём mutation в
  // localStorage-очередь (§3.9) и закрываем wizard как при успехе. drain
  // подхватит её на `online`-event'е из `useFieldOfflineDrain` в
  // FieldTimesheetPage. Без user.id очередь некуда писать — деградируем до
  // обычной ошибки (на /field такое возможно только если /me ещё не успел).
  function enqueueOffline(
    kind: "entry-create" | "entry-update" | "entry-delete",
    method: "POST" | "PUT" | "DELETE",
    path: string,
    body: unknown | undefined,
  ): boolean {
    if (!user) return false;
    enqueue(user.id, { kind, method, path, body, date: activeDate });
    toast.success(t("field.offline.toast.savedLocally"));
    onClose();
    return true;
  }

  const createMut = useMutation({
    mutationFn: (payload: TimesheetEntryPayload) => createFieldEntry(payload, activeDate),
    onMutate: () => setPersistentSaveError(null),
    onSuccess: (entry) => {
      void queryClient.invalidateQueries({ queryKey: ["timesheet", activeDate] });
      onSaved(entry);
    },
    onError: (err: unknown, payload: TimesheetEntryPayload) => {
      if (isNetworkError(err)) {
        const ok = enqueueOffline(
          "entry-create",
          "POST",
          `/field/timesheet/entries?date=${encodeURIComponent(activeDate)}`,
          payload,
        );
        if (ok) return;
      }
      setPersistentSaveError(
        apiErrorFormIssue(err, {
          id: "field-entry-flow-server-error",
          fallbackMessage: t("common.apiError.fallback"),
        }),
      );
      showApiErrorToast(err, { fallbackMessage: t("common.apiError.fallback") });
    },
  });

  const replaceMut = useMutation({
    mutationFn: (args: { oldId: number; payload: TimesheetEntryPayload }) =>
      replaceFieldEntry(args.oldId, args.payload, activeDate),
    onMutate: () => setPersistentSaveError(null),
    onSuccess: (entry) => {
      void queryClient.invalidateQueries({ queryKey: ["timesheet", activeDate] });
      onSaved(entry);
    },
    onError: (err: unknown, args: { oldId: number; payload: TimesheetEntryPayload }) => {
      if (isNetworkError(err)) {
        const ok = enqueueOffline(
          "entry-update",
          "PUT",
          `/field/timesheet/entries/${args.oldId}?date=${encodeURIComponent(activeDate)}`,
          args.payload,
        );
        if (ok) return;
      }
      const presentation = presentApiError(err, {
        fallbackMessage: t("common.apiError.fallback"),
        domainRules: [
          {
            code: "entry_conflict",
            status: 409,
            matches: (detail) =>
              detail === "строка уже изменена в другом запросе" ||
              detail === "отчёт уже подтверждён администратором, правка запрещена",
            message: t("field.entryFlow.toast.conflict"),
          },
        ],
      });
      if (presentation.kind === "domain" && presentation.code === "entry_conflict") {
        toast.error(t("field.entryFlow.toast.conflict"));
        void queryClient.invalidateQueries({ queryKey: ["timesheet", activeDate] });
        onClose();
        return;
      }
      setPersistentSaveError(
        apiErrorFormIssue(err, {
          id: "field-entry-flow-server-error",
          fallbackMessage: t("common.apiError.fallback"),
        }),
      );
      showApiErrorToast(err, { fallbackMessage: t("common.apiError.fallback") });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (entryId: number) => deleteFieldEntry(entryId, activeDate),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["timesheet", activeDate] });
      onDeleted?.();
      onClose();
    },
    onError: (err: unknown, entryId: number) => {
      if (isNetworkError(err)) {
        const ok = enqueueOffline(
          "entry-delete",
          "DELETE",
          `/field/timesheet/entries/${entryId}?date=${encodeURIComponent(activeDate)}`,
          undefined,
        );
        if (ok) return;
      }
      showApiErrorToast(err, { fallbackMessage: t("common.apiError.fallback") });
    },
  });

  return {
    create: (payload) => createMut.mutate(payload),
    replace: (entryId, payload) => replaceMut.mutate({ oldId: entryId, payload }),
    remove: async (entryId) => {
      try {
        await deleteMut.mutateAsync(entryId);
      } catch {
        // The mutation owns the user-facing error and offline fallback.
      }
    },
    isSaving: createMut.isPending || replaceMut.isPending,
    isDeleting: deleteMut.isPending,
    saveError: persistentSaveError,
  };
}
